import {
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { AppInsightsService, AuthService, AvailableDepartment } from '@experian-digital/affordability-library';
import { ImageUtils } from '@experian-digital/affordability-library/src/lib/shared/utils/image.utils';
import { BaseComponent } from '@portal-app/advisor/pages/base/base.component';

import { DropdownMenuItem } from '@experian-digital/affordability-library/src/lib/components/lib-dropdown-menu/lib-dropdown-menu-items.model';
import { APP_ROUTES } from '@portal-app/advisor/routes';
import { HeaderService } from '@portal-shared/services/header.service';
import { STORE_FIELDS } from '@portal-store/models/store.model';
import { Observable } from 'rxjs';
import { ApplicationInsightsEvents } from '../../models/enums/appinsights-events.enum';

@Component({
  selector: 'aff-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HeaderComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() showNavigation? = true;
  hideExperianLogo: boolean;
  squareLogo: boolean;
  isSwitchDeptModalOpen: boolean;
  availableDepartments: AvailableDepartment[] = [];
  departmentsDropDown: DropdownMenuItem[] = [];
  debouncedSetHeaderHeight: () => void;
  @ViewChild('header') myElement: ElementRef;
  private resizeObserver: ResizeObserver;

  globalFeatures$: Observable<any>;
  isFirstInstance: boolean;

  constructor(
    public router: Router,
    injector: Injector,
    private authService: AuthService,
    private headerService: HeaderService,
    private ngZone: NgZone,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.handleSquareLogo();
    this.createDerpartmentDropDown();
  }

  ngAfterViewInit() {
    this.isFirstInstance = this.headerService.activate();
    if (!this.isFirstInstance) {
      return;
    }
    this.setHeaderHeight();

    this.resizeObserver = new ResizeObserver((entries) => {
      this.ngZone.run(() => {
        for (let entry of entries) {
          if (entry.target === this.myElement.nativeElement) {
            this.setHeaderHeight();
          }
        }
      });
    });
    this.resizeObserver.observe(this.myElement.nativeElement);
  }

  private setHeaderHeight() {
    this.headerService.setHeaderHeight(this.myElement.nativeElement.offsetHeight);
  }

  logout(): void {
    AppInsightsService.logEvent(ApplicationInsightsEvents.Logout, { currentUrl: this.router.url });
    this.authService.logOut();
  }

  navigate(path?: string): void {
    if (!path) {
      path = '/' + (this.isWorkReportHybridFeatureOn ? APP_ROUTES.workReport : APP_ROUTES.home);
    }
    if (path.includes('/home')) {
      AppInsightsService.logEvent(ApplicationInsightsEvents.HomeClicked);
    }
    this.router.navigateByUrl(path);
  }

  currentRouteCheck(): boolean {
    if (this.isWorkReportHybridFeatureOn && !this.router.url.includes(APP_ROUTES.hybridPayroll)) {
      return this.currentRouteEquals(this.router, this.appRoutes.workReport);
    } else {
      return this.currentRouteEquals(this.router, this.appRoutes.home);
    }
  }

  logoutAdvisorPortal(): void {
    this.authService.logOut();
  }

  selectDepartment(selectedDepartment: DropdownMenuItem): void {
    /**
     * Remove store then the department resolver, triggered by navigation to home, will then take from local storage
     * Make showNavigation false/unavailable until home is loaded with new department
     */
    this.showNavigation = false;
    this.store.remove(STORE_FIELDS.department);
    localStorage.setItem('departmentName', selectedDepartment.value);

    this.router.navigateByUrl(APP_ROUTES.home).then(() => {
      this.showNavigation = true;
    });
  }

  private handleSquareLogo(): void {
    if (!this.department) {
      return;
    }

    ImageUtils.isSquare(this.department.originalLogo).subscribe((isSquare: boolean) => {
      this.squareLogo = isSquare;
    });
  }

  removeAnimation(): void {
    this.headerService.isBannerAnimationDisabled = true;
  }

  isAniDisabled(): boolean {
    return this.headerService.isBannerAnimationDisabled;
  }

  private createDerpartmentDropDown(): void {
    this.availableDepartments = this.store.get<AvailableDepartment[]>(STORE_FIELDS.availableDepartments);
    this.departmentsDropDown = this.availableDepartments?.map((department: AvailableDepartment) => {
      return {
        id: department.id,
        title: department.name,
        value: department.shortName,
        isSelected: department.shortName === localStorage.getItem('departmentName'),
      };
    });
  }

  get headerTitle(): string {
    return this.isWorkReportHybridFeatureOn ? 'Work Report' : 'Affordability Portal';
  }

  ngOnDestroy() {
    if (this.isFirstInstance) {
      this.headerService.deactivate();
    }
  }
}
